import FormData from "form-data";
import fetchBuilder from "fetch-retry";

export type Broadcast = {
    is_live?: boolean
    air_start_time: string
    air_end_time: string
}

export type Venue = {
    id: number
    name: string
    country_code?: string
    address?: string
    website?: string
}

export type TrailerData = {
    url: string
    size: number
    duration: number
    contentType: string
    directLink: string | null
}

export type groupedMessagesData = {
    replayInfo?: string[]
    custom?: string[]
}

export type Brand = {
    id: number
    name: string
    is_primary?: boolean
}

export type Genre = {
    id: number
    name: string
    is_primary?: boolean
}


type Price = {
    amount: number,
    currency: string
}

export type Billing = {
    is_chargeable: boolean,
    is_purchased?: boolean,
    package_id?: number,
    package_name?: string,
    price?: Price,
    subscription?: string,
    type?: string,
    required_subscription?: string
    has_purchase_options?: boolean
}

type Link = {
    href: string
}

export type ContentImageLink = {
    href: string,
    templated: boolean
}

export type ContentItemLinks = {
    self: Link,
    image?: ContentImageLink,
    poster?: ContentImageLink,
    checkout?: Link,
    purchase_options?: Link,
    web_link?: Link,
    support?: Link
}

interface Cdn {
    policy?: string;
}

interface Advertising {
    client: string;
    schedule: string;
}

export type ContentStream = {
    url: string,
    cdn: Cdn
    advid: string
    sid: string
    enableDvr: boolean
    uid?: number,
    advertising?: Advertising,
}

export type PageData = {
    id: number,
    item_id: string,
    main_item_id: string,
    title: string,
    broadcasts?: Broadcast[],
    // eslint-disable-next-line
    featuredImage?: any[]
}

export interface Language {
    id: string,
    code: string,
    name: string,
    selected: boolean
}

export interface Languages {
    [key: number]: Language,
}

export interface PlayerLogo {
    file: string,
    position: string,
    link?: string,
}

export type VideoItem = {
    id: number,
    title: string,
    report_channel_id: number,
    report_title: string,
    description: string,
    rating_score: number,
    rating_count: number,
    page_url: string,
    trailer?: TrailerData,
    tags: string[],
    groupedMessages?: groupedMessagesData,
    languages?: Language[],
    broadcasts?: Broadcast[]
    video_types: string[]
    venue?: Venue,
    billing?: Billing,
    brands: Brand[],
    genres: Genre[],
    links: ContentItemLinks,
    chat_room?: string,
    logo?: PlayerLogo
}

async function fetchContentStream(itemId: string, deviceType: string) {
    const qs = {
        site: "fite",
        dt: deviceType,
        ts: new Date().getTime().toString()
    }
    const client = fetchBuilder(fetch)
    return client(
        `/embed/settings/${itemId}/?${new URLSearchParams(qs).toString()}`,
        {
            method: "GET",
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'include',
            redirect: 'manual',
            headers: {
                "Content-Type": "application/json"
            },
            retries: 5,
            retryDelay: attempt => {
                return Math.pow(2, attempt) * 1000
            },
            retryOn: [500, 503, 504]
        }
    ).then(result => {
        if (result.status !== 200){
            return Promise.reject(result)
        }
        return result.json()
    })
}

async function fetchContentPlaybackUrls(itemId: string) {
    const qs = {
        site: "fite",
        ts: new Date().getTime().toString(),
        format: "json"
    }

    const client = fetchBuilder(fetch)
    return client(
        `/embed/play/${itemId}.m3u8?${new URLSearchParams(qs).toString()}`,
        {
            method: "GET",
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'include',
            redirect: 'manual',
            headers: {
                "Content-Type": "application/json"
            },
            retries: 5,
            retryDelay: attempt => {
                return Math.pow(2, attempt) * 1000
            },
            retryOn: [500, 503, 504]
        }
    ).then(result => {
        if (result.status !== 200){
            return Promise.reject(result)
        }
        return result.json()
    })
}

async function fetchContentItem(itemId: string) {
    // const settingsUrl = `/api/video/info/${itemId}/?${new URLSearchParams(qs).toString()}`;
    // const fetchOptions = {
    //     method: "GET",
    //     mode: 'cors',
    //     cache: 'no-cache',
    //     credentials: 'include',
    //     redirect: 'manual',
    //     headers: {
    //         "Content-Type": "application/json"
    //     }
    // };

    const url = new URL(`${window.location.origin}/api/video/info/${itemId}/`)

    try {
        const pageRef = localStorage.getItem(itemId)
        if (pageRef) {
            url.searchParams.set('ref', pageRef)
            localStorage.removeItem(itemId)
        }
    }catch(error) {
        console.error(error)
    }
    const client = fetchBuilder(fetch)
    return client(
        url.toString(),
        {
            method: "GET",
            mode: 'cors',
            cache: 'no-cache',
            credentials: 'include',
            redirect: 'manual',
            headers: {
                "Content-Type": "application/json"
            },
            retries: 5,
            retryDelay: attempt => {
                return Math.pow(2, attempt) * 1000
            },
            retryOn: [500, 503, 504]
        }
    )
        .then(result => {
            if (result.status !== 200){
                return Promise.reject(result)
            }
            return result.json()
        })

    // return fetch(settingsUrl, fetchOptions)
    //     .then(result => {
    //         if (result.status !== 200){
    //             return Promise.reject(result)
    //         }
    //         return result.json()
    //     })
}

function rateContentItem(itemId: string, score: number) {
    const url = `/account/rate/${itemId}/`
    const form: HTMLFormElement = document.createElement("form");
    const formData: FormData = new FormData(form)

    formData.append("rating", score)
    return fetch(
        url, {
            method: 'POST',
            cache: 'no-cache',
            credentials: "include",
            body: new URLSearchParams(`rating=${score}`)
        })
        .then(result => {
            if (result.status !== 200){
                return Promise.reject(result)
            }
            return result.json()
        })
}

export {fetchContentItem, fetchContentStream, rateContentItem, fetchContentPlaybackUrls}
